import { useCallback, useState } from 'react';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useClientContext } from 'client/context';
import { selectAdapters } from 'ducks/adapters/selectors';
import { setAdapters } from 'ducks/adapters/slices';
import { selectCandleWorkers } from 'ducks/candle-workers/selectors';
import { updateOverlay } from 'ducks/overlays/slices';
import { selectPairs } from 'ducks/pairs/selectors';
import { setPairs } from 'ducks/pairs/slices';
import { updateSettings } from 'ducks/settings/slices';
import { selectCandleWorkerStatus } from 'ducks/statuses/selectors';
import { setStatuses } from 'ducks/statuses/slices';
import { Grid } from 'framework/Grid';
import { GridFieldCell } from 'framework/Grid/cells/GridCell';
import { GridColumn } from 'framework/Grid/columns/GridColumn';
import { Input } from 'framework/Input';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { filterGrid } from 'utils/strings';

import { GridPairJobCell } from 'components/cells/GridPairJobCell';
import { GridStatusChipCell } from 'components/cells/GridStatusChipCell';
import { GridAutoSizer } from 'components/grid/GridScrollable';
import { AccordionCheckboxes } from 'components/overlays/accordions/accordion-checkboxes/AccordionCheckboxes';
import { CandleWorkersOverlay } from 'components/overlays/candle-workers/CandleWorkersOverlay';
import { Filters } from 'components/overlays/filters/Filters';

import cn from '../CandleWorkersPage.module.scss';

export const CandleWorkersGrid = () => {
  const dispatch = useAppDispatch();
  const voladeum_pairs = useAppSelector(selectPairs);
  const voladeum_adapters = useAppSelector(selectAdapters);
  const voladeum_candle_workers = useAppSelector(selectCandleWorkers);
  const voladeum_candle_worker_status = useAppSelector(selectCandleWorkerStatus);
  const { Voladeum, handleError } = useClientContext();

  const [filter, setFilter] = useState('');

  const onClick = useCallback(async () => {
    try {
      dispatch(updateSettings({ loading: true }));
      const [voladeum_pairs, voladeum_status, voladeum_adapters] = await Promise.all([
        Voladeum.pairs.list(),
        Voladeum.statuses.list(),
        Voladeum.adapters.list(),
      ]);

      dispatch(setPairs(voladeum_pairs.data));
      dispatch(setStatuses(voladeum_status.data));
      dispatch(setAdapters(voladeum_adapters.data));
      dispatch(updateOverlay({ key: 'candle_workers', data: { is_open: true } }));
      dispatch(updateSettings({ loading: false }));
    } catch (err) {
      handleError(err);
    }
  }, [Voladeum, handleError, dispatch]);
  return (
    <div className={cn.grid}>
      <div className={cn.toolbar}>
        <h2>Candle Workers</h2>
        <div className={cn.flex} />
        <Input
          left_icon={faSearch}
          value={filter}
          onChange={(input) => setFilter(input.value)}
          width="300px"
        />
        <Filters onClick={onClick}>
          <CandleWorkersOverlay>
            <AccordionCheckboxes
              label="Adapters"
              options={voladeum_adapters}
              filter_key="candle_worker_adapter_ids"
              value_key="adapter_name"
              id_key="adapter_id"
            />
            <AccordionCheckboxes
              label="Pairs"
              options={voladeum_pairs}
              filter_key="candle_worker_pair_ids"
              value_key="pair_name"
              id_key="pair_id"
            />
            <AccordionCheckboxes
              label="Status"
              options={voladeum_candle_worker_status}
              filter_key="candle_worker_status_ids"
              value_key="status_name"
              id_key="status_id"
            />
          </CandleWorkersOverlay>
        </Filters>
      </div>
      <GridAutoSizer>
        <Grid rows={filterGrid(voladeum_candle_workers, filter)}>
          <GridColumn
            label="Pair"
            sort_field="pair_name"
            sort_direction="desc"
            width={175}
            text_align="left"
          >
            <GridPairJobCell />
          </GridColumn>
          <GridColumn label="Status" sort_field="status_name" width={150}>
            <GridStatusChipCell />
          </GridColumn>
          <GridColumn label="Adapter" sort_field="adapter_name" width={100} grow={1}>
            <GridFieldCell field="adapter_name" />
          </GridColumn>
          <GridColumn label="Since Date" sort_field="since_date" width={150} grow={1}>
            <GridFieldCell field="since_date" />
          </GridColumn>
          <GridColumn label="To Date" sort_field="to_date" width={150} grow={1}>
            <GridFieldCell field="to_date" />
          </GridColumn>
        </Grid>
      </GridAutoSizer>
    </div>
  );
};
